/**
 * The activity picker's map pane. Shows only what is actually verified:
 * options with server-geocoded pins (def.geo), the day's base (.mstay), and
 * the previous stop the travel chips are measured from (.mroute) — so a
 * "🚶 12 min away" chip on a card has a visible geometry behind it.
 *
 * Marker click HIGHLIGHTS the matching card (scrolls it into view); the
 * popup's "Choose" button carries the same act-choose data attributes as the
 * card itself, so committing goes through the one delegated listener with
 * nothing captured. Unpinned options simply aren't here — the pane says how
 * many are missing rather than faking positions.
 */
import L from "leaflet";
import { esc } from "@trip/shared/engine/time";
import type { LatLng } from "@trip/shared/engine/travel";
import type { Act } from "@trip/shared/types";
import { store } from "../store.js";
import { priceLabel } from "./price.js";
import { addSheetCleanup } from "./sheet.js";

export interface PickerMapOption {
  id: string;
  act: Act;
  geo: LatLng;
  /** region code — colors the dot like the trip map */
  reg: string;
}

export interface PickerMapContext {
  dayId: string;
  slotIndex: number | string;
  options: PickerMapOption[];
  /** the day's base, when it has one */
  base: LatLng | null;
  /** where this slot's travel legs start from, when modeled */
  prev: LatLng | null;
  /** options that have no pin and therefore no marker */
  unpinnedCount: number;
}

export function mountPickerMap(pane: HTMLElement, ctx: PickerMapContext): void {
  if (!ctx.options.length) return;
  pane.innerHTML = `<div class="ap-map" id="ap-map"></div>${
    ctx.unpinnedCount > 0
      ? `<div class="ap-mapnote">📍 ${ctx.unpinnedCount} option${ctx.unpinnedCount > 1 ? "s aren't" : " isn't"} pinned yet and can't be shown.</div>`
      : ""
  }`;

  const map = L.map(pane.querySelector<HTMLElement>("#ap-map")!, { scrollWheelZoom: true });
  L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 18,
    attribution: "&copy; OpenStreetMap contributors",
  }).addTo(map);

  const pts: LatLng[] = ctx.options.map((o) => o.geo);
  if (ctx.base) {
    pts.push(ctx.base);
    L.marker(ctx.base, {
      icon: L.divIcon({ className: "", html: `<span class="mstay">🏨</span>`, iconSize: [25, 25], iconAnchor: [12, 12] }),
      zIndexOffset: 500,
    })
      .bindPopup("<b>Your base</b>")
      .addTo(map);
  }
  if (ctx.prev && (!ctx.base || ctx.prev[0] !== ctx.base[0] || ctx.prev[1] !== ctx.base[1])) {
    pts.push(ctx.prev);
    L.marker(ctx.prev, {
      icon: L.divIcon({ className: "", html: `<span class="mroute">●</span>`, iconSize: [24, 24], iconAnchor: [12, 12] }),
      zIndexOffset: 400,
    })
      .bindPopup("<b>Where you'll be coming from</b>")
      .addTo(map);
  }

  for (const o of ctx.options) {
    const color = store.def.rc[o.reg] ?? "#0f7b8f";
    const m = L.marker(o.geo, {
      icon: L.divIcon({ className: "", html: `<span class="mdot" style="background:${color}"></span>`, iconSize: [14, 14], iconAnchor: [7, 7] }),
    }).addTo(map);
    const price = priceLabel(o.act.price, o.act.cat);
    m.bindPopup(
      `<b>${esc(o.act.name)}</b>` +
        (price ? `<br><span style="color:#c0653f;font-weight:700">${esc(price)}</span>` : "") +
        `<br><button class="btn-det btn-sm" style="margin-top:7px" data-action="act-choose" data-act="${esc(o.id)}" data-day="${esc(ctx.dayId)}" data-i="${esc(String(ctx.slotIndex))}">✓ Choose this</button>`
    );
    // clicking a marker highlights the card — it does NOT commit; the popup
    // button (or the card) is the deliberate act
    m.on("click", () => {
      const card = document.getElementById(`apc_${o.id}`);
      if (!card) return;
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      card.classList.add("map-hi");
      setTimeout(() => card.classList.remove("map-hi"), 1600);
    });
  }

  map.fitBounds(L.latLngBounds(pts).pad(0.25));

  // the pane's size changes with the split layout and the mobile segment
  // switch; Leaflet needs telling every time
  const ro = new ResizeObserver(() => map.invalidateSize());
  ro.observe(pane);
  setTimeout(() => map.invalidateSize(), 250);

  addSheetCleanup(() => {
    ro.disconnect();
    map.remove();
  });
}
